import type { MessageKey } from '@dijitalapp/i18n';
import type { GuardianStudySummaryDto, StudyWeekDto } from '@dijitalapp/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat } from '../lib/chat-context';

/** Student study plan (ADR-021): goals, the week's blocks, what is due, and progress. Private by default. */

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul';

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function shiftDay(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
const dayDate = (day: string) => new Date(`${day}T12:00:00`);

function Bar({ percent, label }: { percent: number; label: string }) {
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} style={{ background: 'var(--surface-2)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${percent}%`, height: '100%', background: 'var(--accent)' }} />
    </div>
  );
}

export function StudyPlan() {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const chat = useChat();
  const [start, setStart] = useState(todayLocal());
  const [week, setWeek] = useState<StudyWeekDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [block, setBlock] = useState({ day: todayLocal(), title: '', minutes: '30', goalId: '' });
  const [goal, setGoal] = useState({ title: '', weeklyMinutes: '' });
  const [showGoalForm, setShowGoalForm] = useState(false);

  const load = useCallback(async () => {
    setWeek(await api<StudyWeekDto>('GET', `/study/week?start=${start}&timeZone=${encodeURIComponent(TZ)}`));
  }, [start]);
  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setError(null);
      try {
        await fn();
        await load();
      } catch (err) {
        setError(t(errorMessageKey(err)));
      }
    },
    [load, t],
  );
  useEffect(() => {
    let alive = true;
    api<StudyWeekDto>('GET', `/study/week?start=${start}&timeZone=${encodeURIComponent(TZ)}`).then(
      (w) => alive && setWeek(w),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [start, t, chat.learnVersion]);

  if (!week) return error ? <p className="alert alert-error">{error}</p> : null;

  const dayFmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  const rangeFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
  const p = week.progress;
  const today = todayLocal();

  const addBlock = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/study/blocks', { day: block.day, title: block.title, minutes: Number(block.minutes), goalId: block.goalId || null });
      setBlock({ ...block, title: '' });
    });
  };
  const addGoal = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/study/goals', { title: goal.title, weeklyMinutes: goal.weeklyMinutes ? Number(goal.weeklyMinutes) : null });
      setGoal({ title: '', weeklyMinutes: '' });
      setShowGoalForm(false);
    });
  };

  return (
    <section className="card stack" aria-labelledby="study-plan">
      <div className="row">
        <h2 id="study-plan" style={{ margin: 0 }}>
          {t('study.title')}
        </h2>
        <span className="spacer" />
        <button className="btn" type="button" aria-label={t('study.prevWeek')} onClick={() => setStart(shiftDay(week.start, -7))}>
          ‹
        </button>
        <span>
          {rangeFmt.format(dayDate(week.start))} – {rangeFmt.format(dayDate(week.end))}
        </span>
        <button className="btn" type="button" aria-label={t('study.nextWeek')} onClick={() => setStart(shiftDay(week.start, 7))}>
          ›
        </button>
      </div>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}

      <div className="stack" style={{ gap: 'var(--space-1)' }}>
        <span>{t('study.progress', { done: p.doneMinutes, planned: p.plannedMinutes, blocksDone: p.blocksDone, blocksTotal: p.blocksTotal })}</span>
        <Bar percent={p.plannedMinutes ? Math.round((p.doneMinutes / p.plannedMinutes) * 100) : 0} label={t('study.weekProgress')} />
      </div>

      {week.due.length > 0 && (
        <div className="stack" style={{ gap: 'var(--space-1)' }}>
          <h3 style={{ margin: 0 }}>{t('study.due')}</h3>
          {week.due.map((d) => (
            <div key={d.type + d.id} className="row">
              <span>
                {d.type === 'quiz' ? '📝' : '📄'} <strong>{d.title}</strong> <span className="muted">· {d.className} · {i18n.formatDate(d.dueAt)}</span>
              </span>
              <span className="spacer" />
              {d.status === 'overdue' && <span className="badge">{t('learn.status.missing')}</span>}
              {d.planned && <span className="badge">{t('study.planned')}</span>}
            </div>
          ))}
        </div>
      )}
      <div className="row">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() =>
            void run(async () => {
              const r = await api<{ created: number }>('POST', '/study/week/auto', { start: week.start, timeZone: TZ });
              setNotice(r.created ? t('study.autoCreated', { count: r.created }) : t('study.autoNothing'));
            })
          }
        >
          {t('study.auto')}
        </button>
        {notice && (
          <span className="muted" role="status">
            {notice}
          </span>
        )}
      </div>
      <p className="hint">{t('study.autoNote')}</p>

      {week.goals.length > 0 && (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <h3 style={{ margin: 0 }}>{t('study.goals')}</h3>
          {week.goals.map((g) => (
            <div key={g.goal.id} className="stack" style={{ gap: 'var(--space-1)' }}>
              <div className="row">
                <strong>{g.goal.title}</strong>
                <span className="muted">
                  {g.goal.weeklyMinutes ? t('study.goalLine', { done: g.doneMinutes, target: g.goal.weeklyMinutes }) : t('study.goalNoTarget', { done: g.doneMinutes })}
                </span>
                <span className="spacer" />
                <button className="link-button" type="button" onClick={() => void run(() => api('PATCH', `/study/goals/${g.goal.id}`, { status: 'done' }))}>
                  {t('study.goalDone')}
                </button>
              </div>
              {g.percent !== null && <Bar percent={g.percent} label={g.goal.title} />}
            </div>
          ))}
        </div>
      )}
      {showGoalForm ? (
        <form className="row" onSubmit={addGoal}>
          <div className="field">
            <label htmlFor="sg-title">{t('study.goalTitle')}</label>
            <input id="sg-title" className="input" required maxLength={120} value={goal.title} onChange={(e) => setGoal({ ...goal, title: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="sg-min">{t('study.weeklyMinutes')}</label>
            <input id="sg-min" className="input" type="number" min={15} max={1200} style={{ width: 120 }} value={goal.weeklyMinutes} onChange={(e) => setGoal({ ...goal, weeklyMinutes: e.target.value })} />
          </div>
          <button className="btn btn-primary">{t('study.addGoal')}</button>
        </form>
      ) : (
        <button className="btn" type="button" onClick={() => setShowGoalForm(true)}>
          {t('study.newGoal')}
        </button>
      )}

      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        {week.days.map((d) => (
          <div key={d.date} className="stack" style={{ gap: 'var(--space-1)', borderTop: '1px solid var(--border)', paddingTop: 'var(--space-2)' }}>
            <strong style={{ color: d.date === today ? 'var(--accent)' : undefined }}>{dayFmt.format(dayDate(d.date))}</strong>
            {d.blocks.length === 0 && <span className="muted">—</span>}
            {d.blocks.map((b) => (
              <div key={b.id} className="row">
                <label className="check">
                  <input type="checkbox" checked={b.done} onChange={(e) => void run(() => api('PATCH', `/study/blocks/${b.id}`, { done: e.target.checked }))} />
                  <span style={{ textDecoration: b.done ? 'line-through' : undefined }}>
                    {b.title} <span className="muted">· {t('study.minutes', { count: b.minutes })}</span>
                  </span>
                </label>
                <span className="spacer" />
                <span className="badge">{t(`study.kind.${b.kind}` as MessageKey)}</span>
                <button className="link-button" type="button" aria-label={t('study.removeBlock', { title: b.title })} onClick={() => void run(() => api('DELETE', `/study/blocks/${b.id}`))}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      <form className="row" onSubmit={addBlock}>
        <div className="field">
          <label htmlFor="sb-day">{t('study.day')}</label>
          <select id="sb-day" className="input" value={block.day} onChange={(e) => setBlock({ ...block, day: e.target.value })}>
            {week.days.map((d) => (
              <option key={d.date} value={d.date}>
                {dayFmt.format(dayDate(d.date))}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="sb-title">{t('study.blockTitle')}</label>
          <input id="sb-title" className="input" required maxLength={200} value={block.title} onChange={(e) => setBlock({ ...block, title: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="sb-min">{t('study.minutesLabel')}</label>
          <input id="sb-min" className="input" type="number" min={5} max={240} style={{ width: 90 }} required value={block.minutes} onChange={(e) => setBlock({ ...block, minutes: e.target.value })} />
        </div>
        {week.goals.length > 0 && (
          <div className="field">
            <label htmlFor="sb-goal">{t('study.forGoal')}</label>
            <select id="sb-goal" className="input" value={block.goalId} onChange={(e) => setBlock({ ...block, goalId: e.target.value })}>
              <option value="">—</option>
              {week.goals.map((g) => (
                <option key={g.goal.id} value={g.goal.id}>
                  {g.goal.title}
                </option>
              ))}
            </select>
          </div>
        )}
        <button className="btn btn-primary">{t('study.addBlock')}</button>
      </form>

      <label className="check">
        <input type="checkbox" checked={week.shareWithGuardians} onChange={(e) => void run(() => api('PUT', '/study/settings', { shareWithGuardians: e.target.checked }))} />
        <span>{t('study.share')}</span>
      </label>
      <p className="hint">{t('study.privacy')}</p>
    </section>
  );
}

/** Guardian view of a child's shared plan: totals and goal percentages only. */
export function GuardianStudySummary({ summary }: { summary: GuardianStudySummaryDto | null }) {
  const { i18n } = useApp();
  const { t } = i18n;
  if (!summary) return <p className="hint">{t('study.notShared')}</p>;
  const p = summary.progress;
  return (
    <div className="stack" style={{ gap: 'var(--space-1)' }}>
      <h3 style={{ margin: 0 }}>{t('study.title')}</h3>
      <span>{t('study.progress', { done: p.doneMinutes, planned: p.plannedMinutes, blocksDone: p.blocksDone, blocksTotal: p.blocksTotal })}</span>
      {summary.goals.map((g) => (
        <span key={g.title} className="muted">
          {g.title}
          {g.percent !== null && ` · %${g.percent}`}
        </span>
      ))}
    </div>
  );
}
